import { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useHiringStatus } from "@/hooks/useHiringStatus";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle,
  Loader2,
  Upload,
  X,
  SprayCan,
    ShieldCheck,
    Car,
    MapPin,
    Briefcase,
    Users,
    ArrowLeft,
    Phone,
    Lock,
  } from "lucide-react";
import { z } from "zod";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyPhone from "@/components/StickyPhone";
import { isValidEmail, isValidUsPhone, formatUsPhone } from "@/lib/validation";
import logoAsset from "@/assets/hill-brothers-logo.png.asset.json";

const applicationSchema = z.object({
  firstName: z.string().trim().min(1, "Required").max(50),
  lastName: z.string().trim().min(1, "Required").max(50),
  email: z.string().trim().max(255).refine(isValidEmail, "Invalid email"),
  phone: z.string().trim().max(20).optional().refine(
    (val) => !val || isValidUsPhone(val),
    "Enter a valid US phone number"
  ),
  workType: z.enum(["employee", "contractor"], { required_error: "Select one" }),
  yearsExperience: z.enum(["1-3", "3-5", "5-10", "10+"], { errorMap: () => ({ message: "Please select your years of experience" }) }),
  location: z.string().trim().min(2, "Please enter your city").max(120, "Too long"),
  experienceSummary: z.string().trim().max(2000).optional(),
  canUseSprayer: z.boolean(),
  oshaCertified: z.boolean(),
  hasVehicle: z.boolean(),
  canCommute: z.boolean(),
  portfolioUrl: z.string().trim().url("Invalid URL").max(500).optional().or(z.literal("")),
  message: z.string().trim().max(2000).optional(),
  consentContact: z.literal(true, { errorMap: () => ({ message: "You must consent to be contacted" }) }),
});

type FormData = z.infer<typeof applicationSchema>;

const initialForm: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  workType: "employee",
  yearsExperience: "" as unknown as FormData["yearsExperience"],
  location: "",
  experienceSummary: "",
  canUseSprayer: false,
  oshaCertified: false,
  hasVehicle: false,
  canCommute: false,
  portfolioUrl: "",
  message: "",
  consentContact: false as unknown as true,
};

interface FormErrors { [key: string]: string; }

export default function Careers() {
  const { isHiring, isLoading: hiringLoading } = useHiringStatus();
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [workSamples, setWorkSamples] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const samplesInputRef = useRef<HTMLInputElement>(null);

  // Location autocomplete (US cities via Nominatim / OpenStreetMap)
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationJustPicked, setLocationJustPicked] = useState(false);
  const [locationActiveIdx, setLocationActiveIdx] = useState(-1);
  const locationAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = form.location.trim();
    if (locationJustPicked) { setLocationJustPicked(false); return; }
    if (q.length < 3) { setLocationSuggestions([]); setLocationActiveIdx(-1); return; }
    const handle = setTimeout(async () => {
      try {
        locationAbortRef.current?.abort();
        const ac = new AbortController();
        locationAbortRef.current = ac;
        const url = `https://nominatim.openstreetmap.org/search?format=json&countrycodes=us&addressdetails=1&limit=6&featuretype=city&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { signal: ac.signal, headers: { "Accept": "application/json" } });
        if (!res.ok) return;
        const data: Array<{ address?: { city?: string; town?: string; village?: string; hamlet?: string; county?: string; state?: string; state_code?: string; country_code?: string } }> = await res.json();
        const seen = new Set<string>();
        const opts: string[] = [];
        for (const r of data) {
          const a = r.address || {};
          if ((a.country_code || "").toLowerCase() !== "us") continue;
          const city = a.city || a.town || a.village || a.hamlet || a.county;
          const state = a.state;
          if (!city || !state) continue;
          const label = `${city}, ${state}, US`;
          if (seen.has(label)) continue;
          seen.add(label);
          opts.push(label);
        }
        setLocationSuggestions(opts);
        setShowLocationSuggestions(opts.length > 0);
        setLocationActiveIdx(-1);
      } catch { /* ignore aborts / network */ }
    }, 300);
    return () => clearTimeout(handle);
  }, [form.location, locationJustPicked]);

  const pickLocation = (s: string) => {
    setForm((p) => ({ ...p, location: s }));
    setLocationJustPicked(true);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
    setLocationActiveIdx(-1);
    if (errors.location) setErrors((p) => { const n = { ...p }; delete n.location; return n; });
  };

  const handleLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showLocationSuggestions || locationSuggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setLocationActiveIdx((i) => (i + 1) % locationSuggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setLocationActiveIdx((i) => (i <= 0 ? locationSuggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" || e.key === "Tab" || e.key === " ") {
      const idx = locationActiveIdx >= 0 ? locationActiveIdx : 0;
      if (locationActiveIdx >= 0 || e.key === "Enter") {
        e.preventDefault();
        pickLocation(locationSuggestions[idx]);
      }
    } else if (e.key === "Escape") {
      setShowLocationSuggestions(false);
      setLocationActiveIdx(-1);
    }
  };

  const validateField = (name: string) => {
    const result = applicationSchema.safeParse(form);
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      if (!result.success) {
        const err = result.error.errors.find((er) => er.path[0] === name);
        if (err) next[name] = err.message;
      }
      return next;
    });
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateField(e.target.name);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const nextValue = name === "phone" ? formatUsPhone(value) : value;
    setForm((p) => ({ ...p, [name]: nextValue }));
    if (errors[name]) setErrors((p) => { const n = { ...p }; delete n[name]; return n; });
  };

  const toggleBool = (field: keyof FormData) => {
    setForm((p) => ({ ...p, [field]: !p[field] }));
  };

  const handleResumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ variant: "destructive", title: "File too large", description: "Max 10MB" });
      return;
    }
    setResumeFile(file);
  };

  const handleSamplesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const total = workSamples.length + files.length;
    if (total > 5) {
      toast({ variant: "destructive", title: "Too many files", description: "Max 5 work samples" });
      return;
    }
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast({ variant: "destructive", title: `${f.name} too large`, description: "Max 5MB each" });
        return;
      }
    }
    setWorkSamples((prev) => [...prev, ...files]);
  };

  const removeSample = (idx: number) => setWorkSamples((prev) => prev.filter((_, i) => i !== idx));

  async function uploadFiles(): Promise<{ resumeUrl: string | null; workSamplesUrls: string[] }> {
    const extOf = (name: string) => {
      const dot = name.lastIndexOf(".");
      return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
    };
    let resumeUrl: string | null = null;
    const workSamplesUrls: string[] = [];

    if (resumeFile) {
      const path = `resumes/${crypto.randomUUID()}.${extOf(resumeFile.name)}`;
      const { error } = await supabase.storage.from("application-files").upload(path, resumeFile);
      if (error) {
        console.error("Resume upload failed:", error);
        throw new Error(`Resume upload failed: ${error.message}`);
      }
      resumeUrl = path;
    }

    for (let i = 0; i < workSamples.length; i++) {
      const f = workSamples[i];
      const path = `samples/${crypto.randomUUID()}.${extOf(f.name)}`;
      const { error } = await supabase.storage.from("application-files").upload(path, f);
      if (error) {
        console.error("Sample upload failed:", error);
        throw new Error(`Sample upload failed: ${error.message}`);
      }
      workSamplesUrls.push(path);
    }

    return { resumeUrl, workSamplesUrls };
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = applicationSchema.safeParse(form);
    if (!result.success) {
      const newErrors: FormErrors = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) newErrors[err.path[0] as string] = err.message;
      });
      setErrors(newErrors);
      toast({ variant: "destructive", title: "Please fix the highlighted fields" });
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      setUploading(true);
      const { resumeUrl, workSamplesUrls } = await uploadFiles();
      setUploading(false);

      const { consentContact, ...formPayload } = form;
      const { error } = await supabase.functions.invoke("submit-application", {
        body: {
          ...formPayload,
          consentContact: true,
          resumeUrl,
          workSamplesUrls,
        },
      });

      if (error) throw error;
      setSubmitted(true);
      toast({ title: "Application sent!", description: "We'll be in touch soon." });
    } catch (err) {
      console.error("Application submit failed:", err);
      const message = err instanceof Error && err.message ? err.message : "Please try again or call us.";
      toast({ variant: "destructive", title: "Something went wrong", description: message });
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  if (hiringLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const boolToggles: { key: keyof FormData; label: string; icon: React.ReactNode }[] = [
    { key: "canUseSprayer", label: "Paint sprayer proficiency", icon: <SprayCan className="w-5 h-5" /> },
    { key: "oshaCertified", label: "OSHA certified", icon: <ShieldCheck className="w-5 h-5" /> },
    { key: "hasVehicle", label: "Reliable vehicle", icon: <Car className="w-5 h-5" /> },
    { key: "canCommute", label: "Can work within 1hr of Merrimack, NH", icon: <MapPin className="w-5 h-5" /> },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Painter Jobs in NH | Careers at Hill Brothers</title>
        <meta name="description" content="Hill Brothers is hiring experienced painters with 2+ years of hands-on experience in New Hampshire. Competitive pay, steady work, and a team that treats you like family. Apply today." />
        <meta property="og:title" content="Painter Jobs in NH | Careers at Hill Brothers" />
        <meta property="og:description" content="We're hiring experienced painters (2+ years) in New Hampshire. Competitive pay and steady work across the state." />
        <meta property="og:url" content="https://hillbrothersnh.com/careers" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Painter Jobs in NH | Careers at Hill Brothers" />
        <meta name="twitter:description" content="We're hiring experienced painters (2+ years) in New Hampshire. Apply today." />
        <link rel="canonical" href="https://hillbrothersnh.com/careers" />
        <script type="application/ld+json">{JSON.stringify({
          "@context":"https://schema.org","@type":"BreadcrumbList",
          itemListElement:[
            {"@type":"ListItem",position:1,name:"Home",item:"https://hillbrothersnh.com/"},
            {"@type":"ListItem",position:2,name:"Careers",item:"https://hillbrothersnh.com/careers"}
          ]
        })}</script>
      </Helmet>

      <Header />

      {!isHiring ? (
        <main className="section-container py-32 text-center">
          <h1 className="text-3xl font-heading font-bold text-foreground mb-4">We're Not Currently Hiring</h1>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            Thanks for your interest in Hill Brothers Painting & Contracting. Check back soon. We're always growing!
          </p>
          <Link to="/" className="btn-primary">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </main>
      ) : submitted ? (
        <main className="section-container py-32 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">Application Received!</h1>
            <p className="text-muted-foreground mb-8">
              Thank you for your interest. We'll review your application and reach out if there's a fit.
            </p>
            <Link to="/" className="btn-primary">Back to Home</Link>
          </div>
        </main>
      ) : (
        <>
      {/* Hero */}
          <section className="bg-primary pt-28 pb-16">
            <div className="section-container text-center">
              <Link to="/" className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground mb-4 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <img
                src={logoAsset.url}
                alt="Hill Brothers Painting & Contracting logo"
                width="720"
                height="720"
                loading="eager"
                decoding="async"
                className="w-44 md:w-52 h-auto mb-4 rounded-xl mx-auto"
              />
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary-foreground mb-4">
                Join the Hill Brothers Team
              </h1>
              <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto">
                We're hiring experienced painters with at least 2 years of hands-on painting experience. Competitive pay, steady work across New Hampshire, and a team that treats you like family.
              </p>
            </div>
          </section>

          {/* Perks */}
          <section className="py-16 bg-secondary">
            <div className="section-container">
              <h2 className="section-heading text-center mb-10">Why Join Hill Brothers</h2>
              <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
                {[
                  { icon: <Users className="w-6 h-6 text-primary" />, title: "Great Team", desc: "Work with experienced professionals who have your back." },
                  { icon: <Briefcase className="w-6 h-6 text-primary" />, title: "Steady Work", desc: "Year-round projects across residential & commercial." },
                  { icon: <Phone className="w-6 h-6 text-primary" />, title: "Fair Pay", desc: "Competitive compensation with room to grow." },
                ].map((p, i) => (
                  <div key={i} className="bg-card rounded-xl p-6 text-center" style={{ boxShadow: "var(--shadow-card)" }}>
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      {p.icon}
                    </div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">{p.title}</h3>
                    <p className="text-sm text-muted-foreground">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Application Form */}
          <section id="apply" className="py-16">
            <div className="section-container max-w-2xl">
              <h2 className="section-heading font-heading text-center">Quick Application</h2>
              <p className="section-subheading mx-auto text-center mb-8">
                Fill out the basics below. It only takes a few minutes.
              </p>

              <form onSubmit={handleSubmit} className="bg-card p-6 md:p-8 rounded-xl space-y-6" style={{ boxShadow: "var(--shadow-md)" }}>
                {/* Work Type */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">I'm looking for… <span className="text-destructive">*</span></label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: "employee" as const, label: "Full-time Employment", icon: <Users className="w-5 h-5" /> },
                      { value: "contractor" as const, label: "1099 Contract Work", icon: <Briefcase className="w-5 h-5" /> },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm((p) => ({ ...p, workType: opt.value }))}
                        className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all text-sm font-medium ${
                          form.workType === opt.value
                            ? "border-primary bg-primary/5 text-primary"
                            : "border-border bg-background text-muted-foreground hover:border-primary/40"
                        }`}
                      >
                        {opt.icon}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {errors.workType && <p className="text-destructive text-xs mt-1">{errors.workType}</p>}
                </div>

                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-1">First Name <span className="text-destructive">*</span></label>
                    <Input id="firstName" name="firstName" autoComplete="given-name" value={form.firstName} onChange={handleChange} onBlur={handleBlur} className={errors.firstName ? "border-destructive" : ""} />
                    {errors.firstName && <p className="text-destructive text-xs mt-1">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-1">Last Name <span className="text-destructive">*</span></label>
                    <Input id="lastName" name="lastName" autoComplete="family-name" value={form.lastName} onChange={handleChange} onBlur={handleBlur} className={errors.lastName ? "border-destructive" : ""} />
                    {errors.lastName && <p className="text-destructive text-xs mt-1">{errors.lastName}</p>}
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">Email <span className="text-destructive">*</span></label>
                    <Input id="email" name="email" type="email" autoComplete="email" value={form.email} onChange={handleChange} onBlur={handleBlur} className={errors.email ? "border-destructive" : ""} />
                    {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1">Phone</label>
                    <Input id="phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={handleChange} onBlur={handleBlur} className={errors.phone ? "border-destructive" : ""} />
                    {errors.phone && <p className="text-destructive text-xs mt-1">{errors.phone}</p>}
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-foreground mb-1">
                    City <span className="text-destructive">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="location"
                      name="location"
                      autoComplete="address-level2"
                      value={form.location}
                      onChange={handleChange}
                      onKeyDown={handleLocationKeyDown}
                      onFocus={() => { if (locationSuggestions.length > 0) setShowLocationSuggestions(true); }}
                      onBlur={() => { validateField("location"); setTimeout(() => setShowLocationSuggestions(false), 150); }}
                      placeholder="Start typing your city, e.g. Manchester"
                      role="combobox"
                      aria-expanded={showLocationSuggestions}
                      aria-autocomplete="list"
                      aria-controls="location-suggestions"
                      aria-activedescendant={locationActiveIdx >= 0 ? `location-opt-${locationActiveIdx}` : undefined}
                      className={errors.location ? "border-destructive" : ""}
                    />
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <ul
                        id="location-suggestions"
                        role="listbox"
                        className="absolute z-20 mt-1 w-full bg-popover border border-border rounded-md shadow-md max-h-60 overflow-auto text-sm"
                      >
                        {locationSuggestions.map((s, idx) => (
                          <li key={s} id={`location-opt-${idx}`} role="option" aria-selected={idx === locationActiveIdx}>
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onMouseEnter={() => setLocationActiveIdx(idx)}
                              onClick={() => pickLocation(s)}
                              className={`w-full text-left px-3 py-2 ${idx === locationActiveIdx ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground"}`}
                            >
                              {s}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {errors.location && <p className="text-destructive text-xs mt-1">{errors.location}</p>}
                  <p className="text-xs text-muted-foreground mt-1">Just your city and state, so we know your general area.</p>
                </div>


                {/* Experience */}
                <div>
                  <label htmlFor="yearsExperience" className="block text-sm font-medium text-foreground mb-1">Years of Painting Experience <span className="text-destructive">*</span></label>
                  <Select
                    value={form.yearsExperience}
                    onValueChange={(v) => {
                      setForm((p) => ({ ...p, yearsExperience: v as FormData["yearsExperience"] }));
                      if (errors.yearsExperience) setErrors((p) => { const n = { ...p }; delete n.yearsExperience; return n; });
                    }}
                  >
                    <SelectTrigger id="yearsExperience" className={errors.yearsExperience ? "border-destructive" : ""}>
                      <SelectValue placeholder="Select years of experience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-3">2-3 years</SelectItem>
                      <SelectItem value="3-5">3-5 years</SelectItem>
                      <SelectItem value="5-10">5-10 years</SelectItem>
                      <SelectItem value="10+">10+ years</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.yearsExperience && <p className="text-destructive text-xs mt-1">{errors.yearsExperience}</p>}
                  <p className="text-xs text-muted-foreground mt-1">We're looking for painters with at least 2 years of hands-on experience.</p>
                </div>

                <div>
                  <label htmlFor="experienceSummary" className="block text-sm font-medium text-foreground mb-1">Tell us about your painting experience</label>
                  <Textarea id="experienceSummary" name="experienceSummary" value={form.experienceSummary} onChange={handleChange} onBlur={handleBlur} placeholder="Types of projects, specialties, notable jobs…" rows={3} />
                </div>

                {/* Boolean toggle cards */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Quick Questions</label>
                  <div className="grid grid-cols-2 gap-3">
                    {boolToggles.map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => toggleBool(t.key)}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left text-sm transition-all ${
                          form[t.key]
                            ? "border-primary bg-primary/5 text-foreground"
                            : "border-border bg-background text-muted-foreground hover:border-primary/40"
                        }`}
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          form[t.key] ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                        }`}>
                          {form[t.key] ? <CheckCircle className="w-4 h-4" /> : t.icon}
                        </div>
                        <span className="font-medium">{t.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Portfolio URL */}
                <div>
                  <label htmlFor="portfolioUrl" className="block text-sm font-medium text-foreground mb-1">Portfolio or Website Link</label>
                  <Input id="portfolioUrl" name="portfolioUrl" type="url" value={form.portfolioUrl} onChange={handleChange} onBlur={handleBlur} placeholder="https://…" className={errors.portfolioUrl ? "border-destructive" : ""} />
                  {errors.portfolioUrl && <p className="text-destructive text-xs mt-1">{errors.portfolioUrl}</p>}
                </div>

                {/* Resume Upload */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Resume</label>
                  <input ref={resumeInputRef} type="file" accept=".pdf,.doc,.docx,.rtf,.txt,.odt" className="hidden" onChange={handleResumeChange} />
                  {resumeFile ? (
                    <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg text-sm">
                      <span className="truncate flex-1">{resumeFile.name}</span>
                      <button type="button" onClick={() => { setResumeFile(null); if (resumeInputRef.current) resumeInputRef.current.value = ""; }} className="text-muted-foreground hover:text-destructive">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" className="w-full" onClick={() => resumeInputRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-2" /> Upload Resume (PDF, DOC, RTF, TXT, ODT, max 10MB)
                    </Button>
                  )}
                </div>

                {/* Work Samples */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Work Samples (up to 5 images)</label>
                  <input ref={samplesInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleSamplesChange} />
                  {workSamples.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {workSamples.map((f, i) => (
                        <div key={i} className="flex items-center gap-1 bg-muted/50 rounded px-2 py-1 text-xs">
                          <span className="truncate max-w-[120px]">{f.name}</span>
                          <button type="button" onClick={() => removeSample(i)} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  {workSamples.length < 5 && (
                    <Button type="button" variant="outline" size="sm" onClick={() => samplesInputRef.current?.click()}>
                      <Upload className="w-4 h-4 mr-1" /> Add Images
                    </Button>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground mb-1">Anything else?</label>
                  <Textarea id="message" name="message" value={form.message} onChange={handleChange} onBlur={handleBlur} placeholder="Optional" rows={2} />
                </div>

                {/* Consent Checkbox */}
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consentContact"
                    checked={form.consentContact === true}
                    onCheckedChange={(checked) => {
                      setForm((p) => ({ ...p, consentContact: checked as any }));
                      if (errors.consentContact) setErrors((p) => { const n = { ...p }; delete n.consentContact; return n; });
                    }}
                    className={errors.consentContact ? "border-destructive" : ""}
                  />
                  <label htmlFor="consentContact" className="text-sm text-muted-foreground leading-tight cursor-pointer">
                    I consent to Hill Brothers contacting me by phone, text, or email regarding this application. <span className="text-destructive">*</span>
                  </label>
                </div>
                {errors.consentContact && (
                  <p className="text-destructive text-xs -mt-2">{errors.consentContact}</p>
                )}

                <Button type="submit" className="w-full btn-primary" disabled={submitting}>
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" />{uploading ? "Uploading files…" : "Submitting…"}</>
                  ) : (
                    "Submit Application"
                  )}
                </Button>

                {/* Trust Badge */}
                <div className="flex items-start gap-2 pt-2">
                  <Lock className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Your application is encrypted at rest with AES-256 and is only accessible to our hiring team. We never share your personal information.{" "}
                    <Link to="/privacy" className="text-primary underline hover:no-underline">Privacy Policy</Link>
                  </p>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  By submitting, you agree to our{" "}
                  <Link to="/privacy" className="text-primary underline hover:no-underline">Privacy Policy</Link> and{" "}
                  <Link to="/terms" className="text-primary underline hover:no-underline">Terms of Service</Link>.
                </p>
              </form>
            </div>
          </section>
        </>
      )}

      <Footer />
      <StickyPhone />
    </div>
  );
}
